<script lang="ts">
const SWIPE_IGNORED_TARGETS = 'button,a,input,select,textarea,label,[role="button"]'
const SHEET_CONTENT_SELECTOR = '[data-ui-action-sheet]'
</script>
<script setup lang="ts">
    import { unrefElement, useEventListener } from '@vueuse/core';
    import { useId } from 'vue';
    import { DialogContent, DialogOverlay, DialogPortal, DialogRoot, DialogTitle, DrawerContent, DrawerHandle, DrawerOverlay, DrawerPortal, DrawerRoot, DrawerTitle } from 'reka-ui';
    import useSheetNavigation, { SHEET_CLOSE_DURATION_MS, SHEET_NAVIGATION_KEY } from '~/composables/useSheetNavigation';
    import { useIsMobileLayout } from '~/composables/useIsMobileLayout';
    import { sheetBackdropStyle } from '~/composables/useSheetSwipeBackdrop';

    const props = withDefaults(
        defineProps<{
            open: boolean;
            title: string;
            /**
             * Name of the panel to show first. Set it to turn the sheet into a
             * navigable stack: children then declare UiActionSheetPanel children and
             * use `push` / `pop` to go one level deeper instead of opening a second sheet.
             *
             * Mutually exclusive with the `header` slot: a navigable sheet puts its
             * title row inside each panel, so the title travels with the content and
             * a child level can swap the close button for a back chevron.
             */
            rootPanel?: string;
            /**
             * z-index for both the overlay and the sheet content. Callers that
             * participate in `useModalsStore` (see `UiResponsiveModal`) pass its
             * `getModalZIndex(name)` so the sheet stacks correctly against
             * `Modal.vue` instances and their overlays. Standalone consumers
             * (e.g. the invoice actions kebab) can omit it and keep the previous
             * fixed value.
             */
            zIndex?: number;
            /**
             * Max height of the sheet as a percentage of the viewport height.
             * The design's shared sheet shell defaults to 88%, with a handful
             * of taller sheets (e.g. the line editor) needing 92%.
             */
            maxHeightPercent?: number;
            /**
             * Shows the built-in close icon. The desktop (Dialog) rendering reuses
             * `modal.closeIcon` for pixel parity with `Modal.vue`'s own centered
             * dialogs; the mobile (Drawer) rendering uses its own bare-icon style
             * instead, matching the design (`Devis Mobile.dc.html`: a plain FA6 Pro
             * xmark, `color: var(--fg-4)`, no background/border — not a bordered
             * button). Suppressed automatically on a navigable sheet — each panel
             * owns its own back chevron / close instead (see `rootPanel`). Turn off
             * explicitly when a `header` slot already renders its own close button
             * (e.g. the invoice actions kebab).
             */
            showCloseIcon?: boolean;
        }>(),
        {
            zIndex: 1200,
            maxHeightPercent: 88,
            showCloseIcon: true,
        }
    );

    const emit = defineEmits<{
        (e: 'update:open', value: boolean): void;
    }>();

    // Boundary aligned with the rest of the invoicing form (`< lg`, 1024px),
    // not the 768px default: at 800px-wide the form already renders its
    // mobile layout, so the sheet must not switch to the desktop dialog yet.
    const isDesktop = computed(() => !useIsMobileLayout().value);

    const onOpenChange = (value: boolean) => emit('update:open', value);

    const navigable = computed(() => !!props.rootPanel);
    const nav = useSheetNavigation(computed(() => props.rootPanel));
    provide(SHEET_NAVIGATION_KEY, nav);

    // Reopening a sheet must start at the root, but resetting on close would swap
    // the content back mid-slide-down. Wait for the sheet to be off-screen first.
    let resetTimer: ReturnType<typeof setTimeout> | null = null;
    watch(
        () => props.open,
        (open) => {
            if (resetTimer) {
                clearTimeout(resetTimer);
                // Reopened before that reset ran: run it now, or the sheet would come back
                // on the panel it was closed on rather than on its root.
                if (open) nav.reset();
            }
            resetTimer = open ? null : setTimeout(() => nav.reset(), SHEET_CLOSE_DURATION_MS);
        }
    );
    onUnmounted(() => resetTimer && clearTimeout(resetTimer));

    // reka-ui's swipe gesture never starts from an interactive target (`button, a, input…`),
    // for touch as well as mouse. Base UI, the implementation it is ported from, opts the
    // Drawer out of that rule for touch (`ignoreSelectorWhenTouch: false`) so a sheet made
    // of action buttons can still be pulled down from anywhere, like a native iOS sheet.
    // reka-ui 2.10 exposes no such option, so before its `touchstart` listener reads
    // `event.target` we point it at the button's closest non-interactive ancestor. That
    // ancestor sits inside the same scrollable list, so reka-ui's scroll arbitration
    // (only dismiss when the list is scrolled to the top) keeps working. Taps are not
    // affected: `click` is a separate event and the redirect only exists on this
    // `touchstart` object. Mouse drags keep reka-ui's default and still ignore buttons.
    
    

    // Only attached while the Drawer branch can render — on desktop this document-level
    // capture listener would otherwise run on every touch for no purpose (the Dialog
    // branch has no swipe-to-dismiss gesture to redirect around).
    useEventListener(
        computed(() => (isDesktop.value ? null : document)),
        'touchstart',
        (event: TouchEvent) => {
            const target = event.target as Element | null;
            if (!target?.closest(SHEET_CONTENT_SELECTOR)) return;
            let redirected = target.closest(SWIPE_IGNORED_TARGETS)?.parentElement ?? null;
            while (redirected && redirected.closest(SWIPE_IGNORED_TARGETS)) redirected = redirected.parentElement;
            if (redirected) Object.defineProperty(event, 'target', { value: redirected, configurable: true });
        },
        { capture: true, passive: true }
    );

    // A modal opened from inside a sheet (the waste-center or contact "create" forms, say)
    // is a `Modal.vue` rendered by `layouts` — a sibling of the sheet, not a child. reka-ui
    // treats the sheet as a full modal layer, so that sibling was unusable: the focus scope
    // pulled focus straight back out of its fields, `useHideOthers` marked it inert, and the
    // first pointer-down inside it read as an outside interaction and dismissed the sheet
    // from under the user.
    //
    // While such a modal is on top, the sheet steps down to `modal: false` — no focus trap,
    // no hide-others — and refuses to treat anything outside itself as a dismissal. It is
    // still the sheet the user came from; it is simply no longer the layer in charge.
    //
    // Detected by z-index rather than by name: a sheet that takes a slot in the modal stack
    // gets `getModalZIndex(name)` (see `useSheetModal`), so a higher slot means a modal
    // opened after it. A standalone sheet keeps the default 1200, above the whole
    // `Modal.vue` range, and so never yields — which is the old behaviour, unchanged.
    const modalsStore = useModalsStore();
    const hasModalAbove = computed(() => {
        const latest = modalsStore.getLatestModal();
        if (!latest) return false;
        return modalsStore.getModalZIndex(latest) > props.zIndex;
    });

    // Google Places renders its address suggestions in a `.pac-container` appended to
    // `<body>`, outside the sheet, so a tap on one read as a tap outside: it dismissed the
    // sheet instead of picking the address (the waste-centre form, a sheet on mobile).
    // reka dispatches the outside event on the original target, hence `event.target`.
    const isAddressSuggestion = (event: Event) => (event.target as Element | null)?.closest?.('.pac-container') != null;

    // The layers behind the sheet follow its swipe-to-dismiss (`useSheetSwipeBackdrop`,
    // wired by UiActionSheetSwipeBridge inside the drawer root). Its own tint follows only
    // while this sheet owns the gesture; the app-wide overlays only when nothing else is
    // open underneath — dismissing the sheet then reveals the page, so they go with it.
    // A standalone sheet (default 1200, outside the modal stack) never matches the second
    // condition, which is right: the app-wide overlays are not shown for it at all.
    const backdropOwnerId = useId();
    const sheetContent = ref();
    const getSheetContent = () => (unrefElement(sheetContent) as HTMLElement | null | undefined) ?? null;
    const syncGlobalBackdrop = () => {
        const latest = modalsStore.getLatestModal();
        return modalsStore.modals.length === 1 && !!latest && modalsStore.getModalZIndex(latest) === props.zIndex;
    };
    const overlayStyle = computed(() => sheetBackdropStyle(modalsStore.sheetBackdrop, modalsStore.sheetBackdrop.owner === backdropOwnerId));

    /** Outside interactions belong to the modal on top, not to this sheet. */
    const onOutsideInteraction = (event: Event) => {
        if (hasModalAbove.value || isAddressSuggestion(event)) event.preventDefault();
    };

    defineExpose({ push: nav.push, pop: nav.pop });
</script>

<template>
    <DialogRoot
        v-if="isDesktop"
        :open="props.open"
        :modal="!hasModalAbove"
        @update:open="onOpenChange"
    >
        <DialogPortal>
            <DialogOverlay
                class="fixed inset-0 bg-[#101B29]/[.42]"
                :style="{ zIndex }"
            />
            <DialogContent
                class="fixed left-1/2 top-1/2 flex max-h-[82vh] w-80 -translate-x-1/2 -translate-y-1/2 flex-col rounded-xl bg-white shadow-2xl"
                :style="{ zIndex }"
                @escape-key-down="onOutsideInteraction"
                @pointer-down-outside="onOutsideInteraction"
                @interact-outside="onOutsideInteraction"
            >
                <DialogTitle class="sr-only">{{ props.title }}</DialogTitle>
                <div
                    v-if="showCloseIcon && !navigable"
                    :class="$theme('modal.closeIcon.container')"
                >
                    <button
                        type="button"
                        :aria-label="$t('actions.close')"
                        :class="$theme('modal.closeIcon.button')"
                        @click="onOpenChange(false)"
                    >
                        <i class="fa-regular fa-xmark"></i>
                    </button>
                </div>
                <UiActionSheetViewport :navigable="navigable">
                    <slot name="header" />
                    <slot
                        :push="nav.push"
                        :pop="nav.pop"
                        :can-go-back="nav.canGoBack.value"
                    />
                </UiActionSheetViewport>
            </DialogContent>
        </DialogPortal>
    </DialogRoot>

    <!-- Mobile: reka-ui Drawer (the same primitive shadcn-vue's Drawer is built on).
         Its swipe handling listens to native touch events and calls preventDefault once a
         swipe is engaged, so the browser never turns the gesture into a scroll and never
         fires `pointercancel` mid-drag. It also arbitrates against the nearest scrollable
         ancestor: pulling down only dismisses when that list is scrolled to the top.
         The drag offset is exposed through `--drawer-swipe-movement-y`, which the
         transform below follows; `data-swiping` disables the transition while dragging. -->
    <DrawerRoot
        v-else
        :open="props.open"
        :modal="!hasModalAbove"
        @update:open="onOpenChange"
    >
        <UiActionSheetSwipeBridge
            :owner-id="backdropOwnerId"
            :sync-global-backdrop="syncGlobalBackdrop"
            :get-content="getSheetContent"
        />
        <DrawerPortal>
            <DrawerOverlay
                class="fixed inset-0 bg-[#101B29]/[.42] data-[state=closed]:animate-sheet-fade-out data-[state=open]:animate-sheet-fade-in"
                :style="[{ zIndex }, overlayStyle]"
            />
            <DrawerContent
                ref="sheetContent"
                data-ui-action-sheet
                class="fixed inset-x-0 bottom-0 flex flex-col rounded-t-[20px] bg-white shadow-2xl outline-none [transform:translate3d(0,var(--drawer-swipe-movement-y,0px),0)] transition-transform duration-500 ease-[cubic-bezier(0.32,0.72,0,1)] after:absolute after:inset-x-0 after:top-full after:h-24 after:bg-white after:content-[''] data-[state=closed]:animate-sheet-slide-out data-[state=open]:animate-sheet-slide-in data-[swiping]:select-none data-[swiping]:duration-0"
                :style="{ zIndex, maxHeight: `${maxHeightPercent}%` }"
                @escape-key-down="onOutsideInteraction"
                @pointer-down-outside="onOutsideInteraction"
                @interact-outside="onOutsideInteraction"
            >
                <DrawerTitle class="sr-only">{{ props.title }}</DrawerTitle>
                <DrawerHandle class="mx-auto mb-1 mt-3 h-1 w-10 shrink-0 rounded-full bg-neutral-200" />
                <!-- Title + close on one row (design: `.sheet` is a column flexbox,
                     padding 16px 16px 20px; the title row itself is a plain flex row,
                     `gap:10px`, title `flex:1`, close = bare xmark, no button chrome).
                     Individual sheets no longer render their own title text for this
                     case — see each sheet's own comment for which ones still do
                     (the ones shared with desktop via UiResponsiveModal, since
                     Modal.vue has no equivalent built-in title). -->
                <div
                    v-if="showCloseIcon && !navigable"
                    class="flex items-center gap-2.5 px-4 pb-2 pt-3"
                >
                    <span class="flex-1 truncate font-bricolage text-base font-bold leading-tight text-zinc-900">{{ title }}</span>
                    <button
                        type="button"
                        :aria-label="$t('actions.close')"
                        class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-zinc-500 transition-colors hover:text-zinc-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                        @click="onOpenChange(false)"
                    >
                        <i class="fa-regular fa-xmark text-base"></i>
                    </button>
                </div>
                <UiActionSheetViewport
                    :navigable="navigable"
                    compact-bottom
                >
                    <slot name="header" />
                    <slot
                        :push="nav.push"
                        :pop="nav.pop"
                        :can-go-back="nav.canGoBack.value"
                    />
                </UiActionSheetViewport>
            </DrawerContent>
        </DrawerPortal>
    </DrawerRoot>
</template>

<style>
    /* A modal sheet sets `pointer-events: none` on `<body>` and Google Places appends its
       suggestions there, so they showed but could not be tapped. */
    .pac-container {
        pointer-events: auto;
    }
</style>
